// /var/www/ohcampus_frontend_source/src/pages/SimulatorIncentives.jsx
// ─────────────────────────────────────────────────────────────────────────
//  Choice Entry Simulator — Incentive Module (role-aware embed component)
//  Renders inside IncentiveCenter.  Shows scope-appropriate dashboard,
//  configuration (admin), payouts table with approval flow (admin),
//  per-counselor breakdown (team-lead/admin), and exports for everyone.
// ─────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  IndianRupee, Save, Loader2, Calculator, RefreshCw, FileDown,
  CheckCircle2, XCircle, Send, History, AlertTriangle, Sparkles,
  CalendarClock, Users, FileText, FileSpreadsheet,
  Trophy, Crown, Award, ChevronRight,
} from 'lucide-react';
import { simulatorIncentiveAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import SimulatorLeadsPanel from '../components/SimulatorLeadsPanel';

const inr = (n) => `\u20B9${Number(n || 0).toLocaleString('en-IN')}`;
const STATUS_COLOR = {
  Pending:  'bg-amber-100 text-amber-700 border-amber-200',
  Eligible: 'bg-blue-100 text-blue-700 border-blue-200',
  Approved: 'bg-purple-100 text-purple-700 border-purple-200',
  Released: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  Rejected: 'bg-rose-100 text-rose-700 border-rose-200',
};

const Tile = ({ label, value, sub, tint = 'blue', testId }) => {
  const tones = {
    blue:    'bg-blue-50 text-blue-700',
    green:   'bg-emerald-50 text-emerald-700',
    amber:   'bg-amber-50 text-amber-700',
    purple:  'bg-purple-50 text-purple-700',
    rose:    'bg-rose-50 text-rose-700',
    slate:   'bg-slate-50 text-slate-700',
  };
  return (
    <Card className="border-0 shadow-sm" data-testid={testId}>
      <CardContent className="p-4">
        <p className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full inline-block ${tones[tint]}`}>{label}</p>
        <p className="text-2xl font-semibold text-slate-900 mt-2">{value}</p>
        {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
};

export default function SimulatorIncentives() {
  const { user, isAdmin } = useAuth();
  const designation = user?.designation || '';
  const isTeamLead = designation === 'Team Lead' || designation === 'Admission Manager';
  const canConfigure = !!isAdmin();
  const canViewTeam = canConfigure || isTeamLead;

  // State
  const [tab, setTab] = useState('overview');
  const [config, setConfig] = useState(null);
  const [cfgForm, setCfgForm] = useState({ slab_count: 5, amount_per_slab: 500 });
  const [savingCfg, setSavingCfg] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [counselorRows, setCounselorRows] = useState([]);
  const [signups, setSignups] = useState([]);
  const [signupTotal, setSignupTotal] = useState(0);
  const [payouts, setPayouts] = useState([]);
  const [payoutTotal, setPayoutTotal] = useState(0);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // filters
  const [scope, setScope] = useState(canConfigure ? 'admin' : (canViewTeam ? 'team' : 'me'));
  const [signupStatus, setSignupStatus] = useState('');
  const [payoutStatus, setPayoutStatus] = useState('');

  // dialogs
  const [actionPayout, setActionPayout] = useState(null);
  const [actionRemarks, setActionRemarks] = useState('');
  const [reassignSignup, setReassignSignup] = useState(null);
  const [reassignCounselor, setReassignCounselor] = useState('');
  const [reassignRemarks, setReassignRemarks] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);

  // ─── Leaderboard ──────────────────────────────────────────────────────
  const monthNow = new Date().toISOString().slice(0, 7);
  const [lbMonth, setLbMonth]   = useState(monthNow);
  const [lbScope, setLbScope]   = useState('all');  // all | team
  const [lbStatus, setLbStatus] = useState('released_or_approved');
  const [lb, setLb] = useState(null);
  const lbMonthOptions = (() => {
    const now = new Date();
    const opts = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const lbl = d.toLocaleString('en-US', { month: 'short', year: 'numeric' });
      opts.push({ value: ym, label: lbl });
    }
    return opts;
  })();

  // ─── Data loaders ────────────────────────────────────────────────────
  const loadDashboard = useCallback(async () => {
    try {
      if (canConfigure && scope === 'admin') {
        const r = await simulatorIncentiveAPI.adminDashboard();
        setDashboard(r.data);
      } else if (canViewTeam && (scope === 'team' || scope === 'admin')) {
        const r = await simulatorIncentiveAPI.teamDashboard();
        setDashboard(r.data);
        setCounselorRows(r.data?.counselor_summary || []);
      } else {
        const r = await simulatorIncentiveAPI.meDashboard();
        setDashboard(r.data);
      }
    } catch (e) {
      if (e.response?.status !== 403) {
        toast.error(e.response?.data?.detail || 'Could not load dashboard');
      }
    }
  }, [scope, canConfigure, canViewTeam]);

  const loadConfig = useCallback(async () => {
    try {
      const r = await simulatorIncentiveAPI.getConfig();
      setConfig(r.data);
      setCfgForm({
        slab_count:      r.data.slab_count,
        amount_per_slab: r.data.amount_per_slab,
      });
    } catch (_e) {}
  }, []);

  const loadSignups = useCallback(async () => {
    try {
      const params = { scope, limit: 200 };
      if (signupStatus) params.status = signupStatus;
      const r = await simulatorIncentiveAPI.listSignups(params);
      setSignups(r.data.items || []);
      setSignupTotal(r.data.total || 0);
    } catch (_e) { setSignups([]); setSignupTotal(0); }
  }, [scope, signupStatus]);

  const loadPayouts = useCallback(async () => {
    try {
      const params = { scope, limit: 200 };
      if (payoutStatus) params.status = payoutStatus;
      const r = await simulatorIncentiveAPI.listPayouts(params);
      setPayouts(r.data.items || []);
      setPayoutTotal(r.data.total || 0);
    } catch (_e) { setPayouts([]); setPayoutTotal(0); }
  }, [scope, payoutStatus]);

  const loadAudit = useCallback(async () => {
    if (!canConfigure) return;
    try {
      const r = await simulatorIncentiveAPI.auditLog({ limit: 100 });
      setAudit(r.data.items || []);
    } catch (_e) {}
  }, [canConfigure]);

  const loadLeaderboard = useCallback(async () => {
    try {
      const params = { month: lbMonth, scope: lbScope, status_filter: lbStatus, limit: 10 };
      const r = await simulatorIncentiveAPI.leaderboard(params);
      setLb(r.data);
    } catch (_e) { setLb(null); }
  }, [lbMonth, lbScope, lbStatus]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([
        loadDashboard(), loadConfig(), loadSignups(), loadPayouts(), loadAudit(), loadLeaderboard(),
      ]);
      setLoading(false);
    })();
  }, [loadDashboard, loadConfig, loadSignups, loadPayouts, loadAudit, loadLeaderboard]);

  // Actions
  const saveConfig = async () => {
    if (Number(cfgForm.slab_count) <= 0 || Number(cfgForm.amount_per_slab) <= 0) {
      toast.error('Slab count and amount must be greater than zero');
      return;
    }
    setSavingCfg(true);
    try {
      await simulatorIncentiveAPI.setConfig({
        slab_count:      Number(cfgForm.slab_count),
        amount_per_slab: Number(cfgForm.amount_per_slab),
      });
      toast.success('Slab configuration updated');
      await Promise.all([loadConfig(), loadDashboard(), loadAudit()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save configuration');
    } finally { setSavingCfg(false); }
  };

  const runSync = async () => {
    setBusy(true);
    try {
      const r = await simulatorIncentiveAPI.syncSignups();
      toast.success(`Synced ${r.data.new_signups || 0} new signup(s) · ${r.data.auto_attributed || 0} auto-attributed`);
      await Promise.all([loadDashboard(), loadSignups(), loadPayouts()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally { setBusy(false); }
  };

  const runRecalc = async () => {
    setBusy(true);
    try {
      const r = await simulatorIncentiveAPI.recalculate();
      toast.success(`Recalculated · ${r.data.payouts_created || 0} new payout(s) generated`);
      await Promise.all([loadDashboard(), loadSignups(), loadPayouts(), loadAudit()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Recalc failed');
    } finally { setBusy(false); }
  };

  const doPayoutAction = async () => {
    if (!actionPayout) return;
    const { payout, kind } = actionPayout;
    const remarks = actionRemarks || '';
    setBusy(true);
    try {
      if (kind === 'approve') await simulatorIncentiveAPI.approvePayout(payout.id, { remarks });
      if (kind === 'reject')  await simulatorIncentiveAPI.rejectPayout(payout.id, { remarks });
      if (kind === 'release') await simulatorIncentiveAPI.releasePayout(payout.id, { remarks });
      toast.success(`Payout ${kind === 'approve' ? 'approved' : kind === 'reject' ? 'rejected' : 'released'}`);
      setActionPayout(null); setActionRemarks('');
      await Promise.all([loadDashboard(), loadPayouts(), loadAudit()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally { setBusy(false); }
  };

  const doReassign = async () => {
    if (!reassignSignup || !reassignCounselor) {
      toast.error('Counselor user-id is required');
      return;
    }
    setBusy(true);
    try {
      await simulatorIncentiveAPI.reassignSignup(reassignSignup.id, {
        counselor_id: reassignCounselor.trim(),
        remarks: reassignRemarks || 'Admin manual override',
      });
      toast.success('Signup re-attributed');
      setReassignSignup(null); setReassignCounselor(''); setReassignRemarks('');
      await Promise.all([loadSignups(), loadDashboard()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Re-attribute failed');
    } finally { setBusy(false); }
  };

  const doExport = async (format, kind) => {
    try {
      const r = await simulatorIncentiveAPI.exportBlob({ format, kind, scope });
      const ext  = format === 'excel' ? 'xlsx' : format;
      const mime = format === 'excel'
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : (format === 'pdf' ? 'application/pdf' : 'text/csv');
      const blob = new Blob([r.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `simulator-incentive-${kind}-${scope}-${new Date().toISOString().slice(0,10)}.${ext}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Export ready (${format.toUpperCase()})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    }
  };

  // Render
  if (loading) {
    return (
      <Card className="border-0 shadow-sm" data-testid="sim-inc-loading">
        <CardContent className="p-6 flex items-center justify-center text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading Simulator Incentives…
        </CardContent>
      </Card>
    );
  }

  const cycle = dashboard?.current_cycle || {};

  return (
    <Card className="border-0 shadow-sm" data-testid="simulator-incentives-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
          <Sparkles className="h-5 w-5 text-fuchsia-600" />
          Choice Entry Simulator — Incentives
          {cycle?.cycle_date && (
            <Badge variant="outline" className="ml-2 bg-fuchsia-50 border-fuchsia-200 text-fuchsia-700">
              <CalendarClock className="h-3 w-3 mr-1" /> Next payout: {cycle.cycle_date}
            </Badge>
          )}
          {canConfigure && (
            <div className="ml-auto flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={runSync} disabled={busy} data-testid="sim-sync-btn">
                <RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? 'animate-spin' : ''}`} /> Sync signups
              </Button>
              <Button size="sm" variant="outline" onClick={runRecalc} disabled={busy} data-testid="sim-recalc-btn">
                <Calculator className="h-3.5 w-3.5 mr-1" /> Recalculate
              </Button>
            </div>
          )}
        </CardTitle>
        {cycle?.cycle_cutoff && (
          <p className="text-xs text-slate-500 mt-1">
            Cut-off Tuesday <span className="font-semibold text-slate-700">{(cycle.cycle_cutoff || '').replace('T',' ')}</span> · Payout Saturday <span className="font-semibold text-slate-700">{cycle.cycle_date}</span>
          </p>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile testId="sim-tile-signups"   label="Total Signups"      value={dashboard?.total_signups ?? 0} sub={`Eligible: ${dashboard?.eligible_signups ?? 0}`} tint="blue" />
          <Tile testId="sim-tile-utilized"  label="Utilized"           value={dashboard?.utilized_signups ?? 0} sub={`Carry-fwd: ${dashboard?.carry_forward_signups ?? 0}`} tint="amber" />
          <Tile testId="sim-tile-pending"   label="Pending Incentive"  value={inr(dashboard?.pending_incentives)} sub={`Approved: ${inr(dashboard?.approved_incentives)}`} tint="purple" />
          <Tile testId="sim-tile-released"  label="Released"           value={inr(dashboard?.released_incentives)} sub={`Total: ${inr(dashboard?.total_incentives)}`} tint="green" />
        </div>

        {canConfigure && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile testId="sim-tile-liability" label="Liability (Pending+Approved)" value={inr(dashboard?.total_liability)} tint="rose" />
            <Tile testId="sim-tile-upcoming"  label="Upcoming Cycle Amount"        value={inr(dashboard?.upcoming_amount)} sub={cycle?.cycle_date} tint="purple" />
            <Tile testId="sim-tile-slabs"     label="Completed Slabs (lifetime)"   value={dashboard?.completed_slabs ?? 0} tint="blue" />
            <Tile testId="sim-tile-config"    label="Current Slab"                 value={`${config?.slab_count ?? '—'} → ${inr(config?.amount_per_slab)}`} sub="signups → amount" tint="slate" />
          </div>
        )}

        {/* ── Simulator Lead Capture (quick name+phone, then WhatsApp invite) ─ */}
        <SimulatorLeadsPanel />

        {/* ── Counselor Performance Leaderboard ─────────────────────────── */}
        <Card className="border-0 shadow-sm bg-gradient-to-br from-amber-50 via-white to-fuchsia-50" data-testid="sim-leaderboard-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 flex-wrap">
              <Trophy className="h-5 w-5 text-amber-500" />
              Counselor Performance Leaderboard
              {lb?.totals?.total_amount > 0 && (
                <Badge variant="outline" className="ml-2 bg-amber-50 border-amber-200 text-amber-700">
                  {inr(lb.totals.total_amount)} paid out · {lb.totals.total_counselors} counselor(s)
                </Badge>
              )}
              <div className="ml-auto flex items-center gap-2 flex-wrap">
                <select className="text-xs border border-slate-200 rounded h-8 px-2 bg-white" value={lbMonth} onChange={(e) => setLbMonth(e.target.value)} data-testid="sim-lb-month">
                  {lbMonthOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {canViewTeam && (
                  <select className="text-xs border border-slate-200 rounded h-8 px-2 bg-white" value={lbScope} onChange={(e) => setLbScope(e.target.value)} data-testid="sim-lb-scope">
                    <option value="all">All counselors</option>
                    <option value="team">My team only</option>
                  </select>
                )}
                <select className="text-xs border border-slate-200 rounded h-8 px-2 bg-white" value={lbStatus} onChange={(e) => setLbStatus(e.target.value)} data-testid="sim-lb-status">
                  <option value="released">Released only</option>
                  <option value="released_or_approved">Released + Approved</option>
                  <option value="all">All payouts</option>
                </select>
              </div>
            </CardTitle>
            {lb?.my_rank && !lb.rows.some((r) => r.is_me) && (
              <p className="text-xs text-slate-500 mt-1">
                Your rank: <span className="font-semibold text-fuchsia-700">#{lb.my_rank}</span> · earned <span className="font-semibold">{inr(lb.my_amount)}</span> for {lb.month}
              </p>
            )}
          </CardHeader>
          <CardContent className="pt-0">
            {(!lb || lb.rows.length === 0) ? (
              <div className="text-center text-slate-400 py-6 text-sm" data-testid="sim-lb-empty">
                No payouts to rank for <strong>{lb?.month || lbMonth}</strong> yet.
                <span className="block text-[11px] text-slate-400 mt-1">
                  As soon as paid simulator signups generate payouts, the top earners will appear here.
                </span>
              </div>
            ) : (
              <div className="space-y-2">
                {lb.rows.map((r) => {
                  const podium = r.rank <= 3;
                  const podiumColor = r.rank === 1 ? 'from-amber-400 to-yellow-500'
                                    : r.rank === 2 ? 'from-slate-300 to-slate-400'
                                    : r.rank === 3 ? 'from-orange-300 to-orange-500'
                                    : 'from-slate-200 to-slate-200';
                  const maxAmt = lb.rows[0]?.total_amount || 1;
                  const pct = Math.max(8, Math.round((r.total_amount / maxAmt) * 100));
                  return (
                    <div key={r.counselor_id} className={`relative rounded-lg border ${r.is_me ? 'border-fuchsia-300 bg-fuchsia-50/60' : 'border-slate-100 bg-white'} px-3 py-2 flex items-center gap-3`} data-testid={`sim-lb-row-${r.counselor_id}`}>
                      <div className={`flex items-center justify-center h-9 w-9 rounded-full bg-gradient-to-br ${podiumColor} text-white shadow-sm flex-shrink-0`}>
                        {r.rank === 1 ? <Crown className="h-4 w-4" /> : r.rank <= 3 ? <Award className="h-4 w-4" /> : <span className="text-xs font-bold">#{r.rank}</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900 text-sm truncate">{r.counselor_name}</span>
                          {r.is_me && <Badge className="bg-fuchsia-600 hover:bg-fuchsia-600 text-white text-[10px]" data-testid="sim-lb-you-badge">You</Badge>}
                          {podium && <Badge variant="outline" className="text-[10px] bg-amber-50 border-amber-200 text-amber-700">Top {r.rank}</Badge>}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          {r.signups} signups · {r.utilized} utilized · {r.total_slabs} slab{r.total_slabs === 1 ? '' : 's'} · {r.payout_count} payout{r.payout_count === 1 ? '' : 's'}
                        </div>
                        <div className="mt-1.5 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-1.5 rounded-full bg-gradient-to-r ${podium ? 'from-amber-400 to-fuchsia-500' : 'from-blue-400 to-blue-500'}`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-lg font-bold text-slate-900">{inr(r.total_amount)}</p>
                        <p className="text-[10px] text-slate-500 uppercase tracking-wide">earned</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="overview" data-testid="sim-tab-overview">Signups</TabsTrigger>
            <TabsTrigger value="payouts"  data-testid="sim-tab-payouts">Payouts</TabsTrigger>
            {canViewTeam && <TabsTrigger value="team" data-testid="sim-tab-team">Team Breakdown</TabsTrigger>}
            {canConfigure && <TabsTrigger value="config" data-testid="sim-tab-config">Configuration</TabsTrigger>}
            {canConfigure && <TabsTrigger value="audit"  data-testid="sim-tab-audit">Audit Log</TabsTrigger>}
          </TabsList>

          <TabsContent value="overview" className="space-y-3 pt-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-slate-500">Status</Label>
                <select className="text-xs border border-slate-200 rounded h-8 px-2 bg-white" value={signupStatus} onChange={(e) => setSignupStatus(e.target.value)} data-testid="sim-signup-status-filter">
                  <option value="">All</option>
                  <option>Pending</option>
                  <option>Eligible</option>
                  <option>Approved</option>
                  <option>Released</option>
                  <option>Rejected</option>
                </select>
                {canConfigure && (
                  <>
                    <Label className="text-xs text-slate-500 ml-2">Scope</Label>
                    <select className="text-xs border border-slate-200 rounded h-8 px-2 bg-white" value={scope} onChange={(e) => setScope(e.target.value)} data-testid="sim-scope-filter">
                      <option value="admin">All (Admin)</option>
                      <option value="team">My Team</option>
                      <option value="me">My Own</option>
                    </select>
                  </>
                )}
                <span className="text-xs text-slate-500 ml-2">{signupTotal} signup(s)</span>
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" onClick={() => doExport('csv',   'signups')} data-testid="sim-export-csv">  <FileDown className="h-3 w-3 mr-1" /> CSV</Button>
                <Button size="sm" variant="outline" onClick={() => doExport('excel', 'signups')} data-testid="sim-export-xlsx"> <FileSpreadsheet className="h-3 w-3 mr-1" /> Excel</Button>
                <Button size="sm" variant="outline" onClick={() => doExport('pdf',   'signups')} data-testid="sim-export-pdf">  <FileText className="h-3 w-3 mr-1" /> PDF</Button>
              </div>
            </div>

            <div className="overflow-x-auto border border-slate-100 rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Txn ID</TableHead>
                    <TableHead>Student</TableHead>
                    <TableHead>Counselor</TableHead>
                    <TableHead>Signed up</TableHead>
                    <TableHead>Cycle</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Attribution</TableHead>
                    {canConfigure && <TableHead className="text-right">Action</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {signups.length === 0 ? (
                    <TableRow><TableCell colSpan={canConfigure ? 8 : 7} className="text-center text-slate-400 py-6">No signups in scope</TableCell></TableRow>
                  ) : signups.slice(0, 100).map((s) => (
                    <TableRow key={s.id} data-testid={`sim-signup-row-${s.id}`} className="hover:bg-slate-50">
                      <TableCell className="font-mono text-xs">{s.transaction_id || '—'}</TableCell>
                      <TableCell>
                        <div className="font-medium text-slate-900 text-sm">{s.student_name || '—'}</div>
                        <div className="text-[11px] text-slate-500">{s.student_mobile || s.student_email || ''}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{s.counselor_name || <span className="text-slate-400 italic">Unattributed</span>}</div>
                        <div className="text-[11px] text-slate-400">{s.counselor_id || ''}</div>
                      </TableCell>
                      <TableCell className="text-xs">{(s.signup_date || '').slice(0, 10)}</TableCell>
                      <TableCell className="text-xs">{s.incentive_cycle || '—'}</TableCell>
                      <TableCell><Badge variant="outline" className={`text-[10px] ${STATUS_COLOR[s.incentive_status] || ''}`}>{s.incentive_status}</Badge></TableCell>
                      <TableCell className="text-[11px] text-slate-500">{s.attribution_method || '—'}</TableCell>
                      {canConfigure && (
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" onClick={() => setReassignSignup(s)} data-testid={`sim-reassign-${s.id}`}>
                            Re-assign
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {signups.length > 100 && (
                <p className="text-xs text-slate-400 italic px-3 py-2">Showing first 100 of {signupTotal}. Use exports for full data.</p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="payouts" className="space-y-3 pt-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-slate-500">Status</Label>
                <select className="text-xs border border-slate-200 rounded h-8 px-2 bg-white" value={payoutStatus} onChange={(e) => setPayoutStatus(e.target.value)} data-testid="sim-payout-status-filter">
                  <option value="">All</option>
                  <option>Pending</option>
                  <option>Approved</option>
                  <option>Released</option>
                  <option>Rejected</option>
                </select>
                <span className="text-xs text-slate-500 ml-2">{payoutTotal} payout(s)</span>
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" onClick={() => doExport('csv',   'payouts')} data-testid="sim-export-payout-csv">  <FileDown className="h-3 w-3 mr-1" /> CSV</Button>
                <Button size="sm" variant="outline" onClick={() => doExport('excel', 'payouts')} data-testid="sim-export-payout-xlsx"> <FileSpreadsheet className="h-3 w-3 mr-1" /> Excel</Button>
                <Button size="sm" variant="outline" onClick={() => doExport('pdf',   'payouts')} data-testid="sim-export-payout-pdf">  <FileText className="h-3 w-3 mr-1" /> PDF</Button>
              </div>
            </div>

            <div className="overflow-x-auto border border-slate-100 rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Cycle</TableHead>
                    <TableHead>Counselor</TableHead>
                    <TableHead>Slabs × Amount</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Status</TableHead>
                    {canConfigure && <TableHead className="text-right">Action</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payouts.length === 0 ? (
                    <TableRow><TableCell colSpan={canConfigure ? 6 : 5} className="text-center text-slate-400 py-6">No payouts in scope</TableCell></TableRow>
                  ) : payouts.map((p) => (
                    <TableRow key={p.id} data-testid={`sim-payout-row-${p.id}`} className="hover:bg-slate-50">
                      <TableCell className="text-xs font-mono">{p.cycle_date}</TableCell>
                      <TableCell>{p.counselor_name}</TableCell>
                      <TableCell className="text-xs">
                        {p.slab_count_used} × {inr(p.amount_per_slab)} · {p.signup_ids?.length || 0} signups
                      </TableCell>
                      <TableCell className="text-right font-semibold">{inr(p.total_amount)}</TableCell>
                      <TableCell><Badge variant="outline" className={`text-[10px] ${STATUS_COLOR[p.status] || ''}`}>{p.status}</Badge></TableCell>
                      {canConfigure && (
                        <TableCell className="text-right">
                          <div className="flex items-center gap-1 justify-end">
                            {p.status === 'Pending' && (
                              <>
                                <Button size="sm" variant="outline" onClick={() => { setActionPayout({ payout: p, kind: 'approve' }); setActionRemarks(''); }} data-testid={`sim-approve-${p.id}`}>
                                  <CheckCircle2 className="h-3 w-3 mr-1 text-emerald-600" /> Approve
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => { setActionPayout({ payout: p, kind: 'reject' }); setActionRemarks(''); }} data-testid={`sim-reject-${p.id}`}>
                                  <XCircle className="h-3 w-3 mr-1 text-rose-600" /> Reject
                                </Button>
                              </>
                            )}
                            {p.status === 'Approved' && (
                              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => { setActionPayout({ payout: p, kind: 'release' }); setActionRemarks(''); }} data-testid={`sim-release-${p.id}`}>
                                <Send className="h-3 w-3 mr-1" /> Release
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {canViewTeam && (
            <TabsContent value="team" className="space-y-3 pt-3">
              <p className="text-xs text-slate-500">
                Per-counselor breakdown of simulator signups, utilization, and released incentive within your scope.
              </p>
              <div className="overflow-x-auto border border-slate-100 rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Counselor</TableHead>
                      <TableHead>Signups</TableHead>
                      <TableHead>Utilized</TableHead>
                      <TableHead>Carry-Forward</TableHead>
                      <TableHead className="text-right">Released ₹</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {counselorRows.length === 0 ? (
                      <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-6">No counselors in scope</TableCell></TableRow>
                    ) : counselorRows.map((r) => (
                      <TableRow key={r.counselor_id || r.counselor} data-testid={`sim-team-row-${r.counselor_id || 'na'}`}>
                        <TableCell className="font-medium">{r.counselor || 'Unassigned'}<span className="block text-[11px] text-slate-400 font-mono">{r.counselor_id}</span></TableCell>
                        <TableCell>{r.total_signups}</TableCell>
                        <TableCell>{r.utilized}</TableCell>
                        <TableCell>{r.carry_forward}</TableCell>
                        <TableCell className="text-right font-semibold text-emerald-700">{inr(r.released_amt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          )}

          {canConfigure && (
            <TabsContent value="config" className="space-y-4 pt-3">
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-900">
                Formula: <strong>FLOOR(Eligible Signups ÷ Slab Count) × Amount Per Slab</strong>.
                Changes apply to future signups. Previous configurations are preserved in history.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label>Signup slab count</Label>
                  <Input type="number" min={1} value={cfgForm.slab_count} onChange={(e) => setCfgForm({ ...cfgForm, slab_count: e.target.value })} data-testid="sim-cfg-slab" />
                </div>
                <div>
                  <Label>Amount per slab (₹)</Label>
                  <Input type="number" min={1} value={cfgForm.amount_per_slab} onChange={(e) => setCfgForm({ ...cfgForm, amount_per_slab: e.target.value })} data-testid="sim-cfg-amount" />
                </div>
                <div className="flex items-end gap-2">
                  <Button onClick={saveConfig} disabled={savingCfg} className="bg-[#0066CC] hover:bg-[#0052A3]" data-testid="sim-cfg-save">
                    {savingCfg ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                    Save
                  </Button>
                  <Button variant="outline" onClick={() => setHistoryOpen(true)} data-testid="sim-cfg-history">
                    <History className="h-4 w-4 mr-1" /> History
                  </Button>
                </div>
              </div>
              <p className="text-xs text-slate-500 flex items-center gap-1">
                <IndianRupee className="h-3 w-3" /> Currently active: <strong>{config?.slab_count}</strong> signups → <strong>{inr(config?.amount_per_slab)}</strong>
                {config?.updated_at && <span className="ml-2 text-slate-400">(updated {(config.updated_at || '').slice(0, 16).replace('T', ' ')} by {config.updated_by})</span>}
              </p>
            </TabsContent>
          )}

          {canConfigure && (
            <TabsContent value="audit" className="space-y-3 pt-3">
              <p className="text-xs text-slate-500">Every config change, payout transition and signup re-attribution is logged here.</p>
              <div className="overflow-x-auto border border-slate-100 rounded-lg max-h-96 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Time</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Actor</TableHead>
                      <TableHead>Target</TableHead>
                      <TableHead>Remarks</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {audit.length === 0 ? (
                      <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-6">No audit entries</TableCell></TableRow>
                    ) : audit.map((a) => (
                      <TableRow key={a.id} className="hover:bg-slate-50" data-testid={`sim-audit-row-${a.id}`}>
                        <TableCell className="text-[11px] font-mono">{(a.timestamp || '').slice(0,19).replace('T',' ')}</TableCell>
                        <TableCell><Badge variant="outline" className="text-[10px]">{a.action}</Badge></TableCell>
                        <TableCell className="text-xs">{a.actor_name}<span className="block text-[10px] text-slate-400">{a.actor_role}</span></TableCell>
                        <TableCell className="text-[11px] font-mono">{a.target_id || '—'}</TableCell>
                        <TableCell className="text-xs text-slate-600">{a.remarks || ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </CardContent>

      <Dialog open={!!actionPayout} onOpenChange={(o) => { if (!o) { setActionPayout(null); setActionRemarks(''); } }}>
        <DialogContent className="sm:max-w-md" data-testid="sim-action-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {actionPayout?.kind === 'approve' && <><CheckCircle2 className="h-5 w-5 text-emerald-600" /> Approve Payout</>}
              {actionPayout?.kind === 'reject'  && <><XCircle className="h-5 w-5 text-rose-600" /> Reject Payout</>}
              {actionPayout?.kind === 'release' && <><Send className="h-5 w-5 text-emerald-600" /> Release Payout</>}
            </DialogTitle>
            <DialogDescription>
              {actionPayout && (
                <>
                  Counselor <strong>{actionPayout.payout.counselor_name}</strong> · Cycle <strong>{actionPayout.payout.cycle_date}</strong> · Amount <strong>{inr(actionPayout.payout.total_amount)}</strong>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label>Remarks (optional)</Label>
            <Input value={actionRemarks} onChange={(e) => setActionRemarks(e.target.value)} placeholder="Internal note for audit log" data-testid="sim-action-remarks" />
          </div>
          {actionPayout?.kind === 'reject' && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              Rejected payouts release their signups back to the pool so they can re-qualify in a future cycle.
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setActionPayout(null); setActionRemarks(''); }}>Cancel</Button>
            <Button onClick={doPayoutAction} disabled={busy}
              className={actionPayout?.kind === 'reject' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-emerald-600 hover:bg-emerald-700'}
              data-testid="sim-action-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reassignSignup} onOpenChange={(o) => { if (!o) setReassignSignup(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="sim-reassign-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5" /> Re-attribute signup</DialogTitle>
            <DialogDescription>
              {reassignSignup && <>Move <strong>{reassignSignup.student_name}</strong> from <strong>{reassignSignup.counselor_name || 'Unassigned'}</strong> to a different counselor.</>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>New counselor ID</Label>
              <Input value={reassignCounselor} onChange={(e) => setReassignCounselor(e.target.value)} placeholder="e.g. counselor-1" data-testid="sim-reassign-counselor" />
              <p className="text-[11px] text-slate-500 mt-1">User id from the Users tab. The signup will only become eligible in the next cycle.</p>
            </div>
            <div>
              <Label>Remarks (optional)</Label>
              <Input value={reassignRemarks} onChange={(e) => setReassignRemarks(e.target.value)} placeholder="Reason for the manual override" data-testid="sim-reassign-remarks" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignSignup(null)}>Cancel</Button>
            <Button onClick={doReassign} disabled={busy} className="bg-[#0066CC] hover:bg-[#0052A3]" data-testid="sim-reassign-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Re-attribute
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid="sim-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5" /> Slab Configuration History</DialogTitle>
            <DialogDescription>Past versions of slab count + amount per slab. Most recent first.</DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto border border-slate-100 rounded-lg">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Effective from</TableHead>
                  <TableHead>Previous</TableHead>
                  <TableHead>New</TableHead>
                  <TableHead>By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(config?.history || []).slice().reverse().map((h, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="text-xs font-mono">{(h.effective_from || '').slice(0, 16).replace('T', ' ')}</TableCell>
                    <TableCell className="text-xs">{h.previous_slab ?? '—'} → {inr(h.previous_amount)}</TableCell>
                    <TableCell className="text-xs font-semibold">{h.new_slab} → {inr(h.new_amount)}</TableCell>
                    <TableCell className="text-xs">{h.updated_by}</TableCell>
                  </TableRow>
                ))}
                {(config?.history || []).length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-4">No previous versions yet</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
